import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface MdmPinResultDialogProps {
    /** Passcode a mostrar; con `undefined` el diálogo no se abre. */
    pin?: string;
    validitySeconds?: number;
    onClose: () => void;
}

/**
 * Lo que el PIN NO hace. El candado sigue vigente del lado del proveedor, así que el equipo se
 * vuelve a bloquear solo y el expediente no cambia.
 */
export const PIN_DOES_NOT_UNLOCK =
    'Esto NO levanta el candado: abre el equipo un rato y se vuelve a bloquear solo, porque el bloqueo sigue vigente del lado del proveedor. El desbloqueo de verdad es «Desbloquear». El expediente no cambia.';

/** Convierte la vigencia del proveedor a un texto legible; sin dato no inventa una duración. */
export function formatPinValidity(validitySeconds?: number): string | null {
    if (!validitySeconds || validitySeconds <= 0) return null;
    if (validitySeconds < 60) return `Vence en ${String(validitySeconds)} segundos.`;
    const minutes = Math.round(validitySeconds / 60);
    return `Vence en ${String(minutes)} ${minutes === 1 ? 'minuto' : 'minutos'}.`;
}

/**
 * Muestra el passcode de desbloqueo una sola vez. No se guarda en ningún lado: al cerrar se pierde
 * y hay que pedir uno nuevo, que invalida al anterior.
 */
export function MdmPinResultDialog({ pin, validitySeconds, onClose }: MdmPinResultDialogProps) {
    const [copied, setCopied] = useState(false);
    const validity = formatPinValidity(validitySeconds);

    const copy = () => {
        void navigator.clipboard.writeText(pin ?? '').then(
            () => {
                setCopied(true);
            },
            () => {
                setCopied(false);
            },
        );
    };

    return (
        <Dialog
            open={pin !== undefined}
            onOpenChange={(open) => {
                if (!open) {
                    setCopied(false);
                    onClose();
                }
            }}
        >
            <DialogContent className="sm:max-w-[420px]">
                <DialogHeader>
                    <DialogTitle>PIN de desbloqueo</DialogTitle>
                    <DialogDescription>
                        El cliente lo escribe en la pantalla de bloqueo del equipo. Funciona sin
                        señal.
                    </DialogDescription>
                </DialogHeader>

                <div className="my-3 rounded-lg border-2 border-warning bg-warning/10 p-[18px] text-center">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-warning">
                        PIN de desbloqueo
                    </div>
                    <div className="mt-2 font-mono text-[32px] font-bold tracking-[4px] text-foreground">
                        {pin}
                    </div>
                    {validity ? (
                        <div className="mt-2.5 text-[10.5px] text-muted-foreground">{validity}</div>
                    ) : null}
                    <p className="mt-3 text-left text-[11px] font-semibold leading-relaxed text-warning">
                        {PIN_DOES_NOT_UNLOCK}
                    </p>
                </div>

                <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                    <strong className="text-foreground">Dato sensible.</strong> Compártelo solo con
                    el cliente por un canal seguro: una llamada verificada o un WhatsApp confirmado.
                    El PIN se omite de la bitácora y esta pantalla es el único lugar donde se
                    muestra.
                </p>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={copy}>
                        {copied ? 'Copiado' : 'Copiar'}
                    </Button>
                    <Button onClick={onClose}>Cerrar · el PIN no se recupera</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
